import Vue from 'vue';
import gql from 'graphql-tag';
import { client as apolloClient } from '@/apollo/vue-apollo';

// Full action detail query
const ACTION_DETAIL_QUERY = gql`
	query getActionDetail($reference: uuid!) {
		action: risolto_action_by_pk(reference: $reference) {
			reference
			participant
			recipient
			legal_entity
			primary_invoice
			status
			channel
			message_code
			is_retry
			execution_type
			source
			retry_count
			original_action
			start_time
			created_at
			executed_at
			canceled_at
			updated_at
			due_date
			metadata
			assignee
			custom_fields
			assignee_by_reference {
				reference
				username
			}
			recipient_by_reference {
				reference
				name
				account_id
				legal_id
				legal_id_type
				type
				participant
			}
			primary_invoice_by_reference {
				invoice_number
			}
			legal_entity_by_reference {
				reference
				registration_name
				outbound_channel: processing_details(path: "outbound_channel")
			}
			channel_endpoint_by_reference {
				channel
				endpoint
				endpoint_details
				deactivated_at
				deactivated_by
				last_failed_delivery_at
				last_failed_delivery_type
				locale
				priority
			}
			notes(order_by: { created_at: desc }, limit: 1) {
				reference
				note
				category
				created_at
			}
		}
	}
`;

export interface ActionsState {
	actionsByReference: Record<string, any>;
	selectedActionRef: string | null;
	detailLoading: boolean;
	createPhoneActionOverlayVisible: boolean;
	createPhoneActionOverlayInvoices: any[] | null;
	createPhoneActionOverlayRecipient: any | null;
	actionDetailRightPanelCollapsed: boolean;
}

const state: ActionsState = {
	actionsByReference: {},
	selectedActionRef: null,
	detailLoading: false,
	createPhoneActionOverlayVisible: false,
	createPhoneActionOverlayInvoices: null,
	createPhoneActionOverlayRecipient: null,
	actionDetailRightPanelCollapsed: false
};

const getters = {
	actionByRef: (state: ActionsState) => (ref: string) => {
		return state.actionsByReference[ref];
	},

	selectedAction: (state: ActionsState) => {
		if (!state.selectedActionRef) return null;
		return state.actionsByReference[state.selectedActionRef];
	},

	actionsList: (state: ActionsState) => {
		return Object.values(state.actionsByReference);
	}
};

const mutations = {
	UPSERT_ACTION(state: ActionsState, action) {
		const existing = state.actionsByReference[action.reference];
		Vue.set(state.actionsByReference, action.reference, {
			...existing,
			...action
		});
	},

	UPSERT_ACTIONS(state: ActionsState, actions: Array<any>) {
		actions.forEach(action => {
			const existing = state.actionsByReference[action.reference];
			Vue.set(state.actionsByReference, action.reference, {
				...existing,
				...action
			});
		});
	},

	SET_SELECTED_ACTION_REF(state: ActionsState, ref: string | null) {
		state.selectedActionRef = ref;
	},

	SET_DETAIL_LOADING(state: ActionsState, loading: boolean) {
		state.detailLoading = loading;
	},

	CLEAR_ACTIONS(state: ActionsState) {
		state.actionsByReference = {};
		state.selectedActionRef = null;
	},

	REMOVE_ACTION(state: ActionsState, ref: string) {
		Vue.delete(state.actionsByReference, ref);
		if (state.selectedActionRef === ref) {
			state.selectedActionRef = null;
		}
	},

	SET_CREATE_PHONE_ACTION_OVERLAY_VISIBLE(state: ActionsState, visible: boolean) {
		state.createPhoneActionOverlayVisible = visible;
	},

	SET_CREATE_PHONE_ACTION_OVERLAY_INVOICES(state: ActionsState, invoices: any[] | null) {
		state.createPhoneActionOverlayInvoices = invoices;
	},

	SET_CREATE_PHONE_ACTION_OVERLAY_RECIPIENT(state: ActionsState, recipient: any | null) {
		state.createPhoneActionOverlayRecipient = recipient;
	},

	SET_ACTION_DETAIL_RIGHT_PANEL_COLLAPSED(state: ActionsState, collapsed: boolean) {
		state.actionDetailRightPanelCollapsed = collapsed;
	}
};

const actions = {
	async fetchActionDetails({ commit }, reference: string) {
		commit('SET_DETAIL_LOADING', true);
		try {
			const result = await apolloClient.query({
				query: ACTION_DETAIL_QUERY,
				variables: { reference },
				fetchPolicy: 'network-only'
			});
			if (result.data?.action) {
				commit('UPSERT_ACTION', result.data.action);
			}
			return result.data?.action;
		} finally {
			commit('SET_DETAIL_LOADING', false);
		}
	}
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions
};
